import { Html5Qrcode } from 'html5-qrcode';
import { Camera, Keyboard, QrCode } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PageHeader } from '../../components/AppShell';
import { Button, Callout, Card, Field, Input } from '../../components/ui';
import { ApiError, api } from '../../lib/api';

/**
 * QR scanner (directive §10).
 *
 * Labels encode {APP_URL}/m/{token}. Scanning navigates there; the landing page
 * resolves the token, requiring sign-in first if needed. Manual entry by
 * machine ID is always available — a scratched label must not stop a check.
 */
export function ScanPage() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    return () => {
      // Release the camera when leaving the screen, or it stays on.
      const scanner = scannerRef.current;
      if (scanner) {
        scanner.stop().catch(() => undefined).finally(() => scanner.clear());
        scannerRef.current = null;
      }
    };
  }, []);

  const handleDecoded = (text: string) => {
    const token = extractToken(text);
    if (!token) {
      setError('That QR code is not an ODR machine label.');
      return;
    }
    void stop();
    navigate(`/m/${token}`);
  };

  const start = async () => {
    setError(null);
    if (!containerRef.current) return;
    try {
      const scanner = new Html5Qrcode(containerRef.current.id, { verbose: false });
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        handleDecoded,
        () => undefined, // per-frame decode misses are normal; ignore them
      );
      setScanning(true);
    } catch {
      setError(
        'Could not open the camera. Check that ODR is allowed to use it in your browser settings, or type the machine ID below.',
      );
    }
  };

  const stop = async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    try {
      await scanner.stop();
      scanner.clear();
    } catch {
      // Already stopped.
    }
    scannerRef.current = null;
    setScanning(false);
  };

  const openByCode = async () => {
    const code = manual.trim().toUpperCase();
    if (!code) return;
    setBusy(true);
    setError(null);
    try {
      const machine = await api.get<{ id: string }>(
        `/api/machines/by-code/${encodeURIComponent(code)}`,
      );
      navigate(`/machines/${machine.id}`);
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.status === 404
          ? `No machine found with ID "${code}".`
          : caught instanceof ApiError
            ? caught.message
            : 'Could not look up that machine.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Scan a machine"
        subtitle="Point the camera at the QR label on the machine"
      />

      <div className="space-y-4 p-4 lg:p-6">
        {error ? <Callout tone="warn">{error}</Callout> : null}

        <Card className="overflow-hidden">
          <div
            id="odr-qr-reader"
            ref={containerRef}
            className="aspect-square w-full bg-steel-900"
          />
          <div className="p-4">
            {scanning ? (
              <Button variant="secondary" fullWidth size="lg" onClick={() => void stop()}>
                Stop camera
              </Button>
            ) : (
              <Button fullWidth size="lg" icon={Camera} onClick={() => void start()}>
                Start camera
              </Button>
            )}
          </div>
        </Card>

        <Card className="p-4">
          <h2 className="flex items-center gap-2 text-sm font-bold text-steel-900">
            <Keyboard aria-hidden className="size-4" />
            Or type the machine ID
          </h2>
          <p className="mt-1 text-sm text-steel-500">
            Use this if the label is damaged or the camera is not available.
          </p>
          <div className="mt-3 flex gap-2">
            <Field label="Machine ID" className="flex-1">
              <Input
                value={manual}
                onChange={(event) => setManual(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void openByCode();
                }}
                placeholder="FILL-01"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="go"
              />
            </Field>
          </div>
          <Button
            className="mt-2"
            fullWidth
            size="lg"
            icon={QrCode}
            loading={busy}
            disabled={!manual.trim()}
            onClick={() => void openByCode()}
          >
            Open machine
          </Button>
        </Card>
      </div>
    </div>
  );
}

/** Accept a full label URL, a bare token, or a pasted /m/... path. */
function extractToken(text: string): string | null {
  const trimmed = text.trim();
  const match = trimmed.match(/\/m\/([A-Za-z0-9_-]{10,64})/);
  if (match) return match[1] ?? null;
  if (/^[A-Za-z0-9_-]{16,64}$/.test(trimmed)) return trimmed;
  return null;
}
